import { useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';

import { Translations } from '@lifeserver/shared';

import { PortalLocalizationDTO } from '../../hooks/portal';

export interface CategoryLocalizationKeyModalProps {
    open: boolean;
    /** Current key the category is bound to (e.g. `category.<uuid>.name`). */
    value: string;
    /** Auto-derived fallback key used when no override is set. */
    defaultKey: string;
    allLocalizations: PortalLocalizationDTO[];
    sheetTranslations?: Translations;
    onClose: () => void;
    onApply: (key: string) => void;
}

/**
 * Lets editors point a category at a different translation key — either
 * by picking one that already exists in the database (shared usage) or
 * by typing a brand-new identifier (new key created on save).
 */
export const CategoryLocalizationKeyModal = (
    props: CategoryLocalizationKeyModalProps,
) => {
    // Mount-on-open: the body owns its own draft state so the user can
    // freely experiment and back out with Cancel. By only rendering the
    // body while `open` is true, React unmounts it on close which gives
    // us a fresh draft on every reopen without an effect.
    if (!props.open) {
        return null;
    }
    return <CategoryLocalizationKeyModalBody {...props} />;
};

const CategoryLocalizationKeyModalBody = ({
    value,
    defaultKey,
    allLocalizations,
    sheetTranslations,
    onClose,
    onApply,
}: CategoryLocalizationKeyModalProps) => {
    const [draft, setDraft] = useState(value);

    const options = useMemo(() => {
        // A key can have translations in the database, the Google Sheet, or
        // both — the editor doesn't care which, since picking one saves its
        // translations to the portal anyway. Keying by the translation `id`
        // means a key present in BOTH sources collapses into a single option
        // (never listed twice); the language count is the union across both.
        const languages = new Map<string, Set<string>>();

        const addLanguage = (id: string, language: string) => {
            const set = languages.get(id) ?? new Set<string>();
            set.add(language);
            languages.set(id, set);
        };

        for (const loc of allLocalizations) {
            addLanguage(loc.id, loc.language);
        }
        for (const [id, table] of Object.entries(sheetTranslations ?? {})) {
            for (const [language, translation] of Object.entries(table)) {
                if (translation?.trim()) {
                    addLanguage(id, language);
                }
            }
        }

        return Array.from(languages.entries())
            .map(([id, langs]) => ({ id, languageCount: langs.size }))
            .sort((a, b) => a.id.localeCompare(b.id));
    }, [allLocalizations, sheetTranslations]);

    const isUsingDefault = draft === defaultKey || draft.trim() === '';
    const matchingOption = options.find((option) => option.id === draft);
    const isNewKey = !isUsingDefault && !matchingOption;

    const handleReset = () => {
        setDraft(defaultKey);
    };

    const handleApply = () => {
        const next = draft.trim();
        onApply(next || defaultKey);
        onClose();
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Translation key</DialogTitle>
            <DialogContent dividers={false}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        pt: 1,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Pick an existing key to share translations with another
                        entry, or type a brand-new identifier to create one.
                        Choosing an existing key saves its translations to the
                        portal on save. Leave blank to fall back to the
                        auto-generated key.
                    </Typography>

                    <Autocomplete
                        freeSolo
                        size="small"
                        value={draft}
                        options={options}
                        getOptionLabel={(option) =>
                            typeof option === 'string' ? option : option.id
                        }
                        isOptionEqualToValue={(option, val) =>
                            typeof val === 'string'
                                ? option.id === val
                                : option === val
                        }
                        renderOption={(props, option) => {
                            const { key, ...other } = props;
                            return (
                                <li {...other} key={key}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minWidth: 0,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: 'monospace',
                                                wordBreak: 'break-all',
                                            }}
                                        >
                                            {option.id}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            {option.languageCount} translation
                                            {option.languageCount === 1
                                                ? ''
                                                : 's'}{' '}
                                            on file
                                        </Typography>
                                    </Box>
                                </li>
                            );
                        }}
                        onChange={(_, newValue) => {
                            if (newValue === null) {
                                setDraft(defaultKey);
                                return;
                            }
                            setDraft(
                                typeof newValue === 'string'
                                    ? newValue
                                    : newValue.id,
                            );
                        }}
                        onInputChange={(_, newInput, reason) => {
                            if (reason === 'input') {
                                setDraft(newInput);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Translation Key"
                                placeholder={defaultKey}
                                autoFocus
                            />
                        )}
                    />

                    {isUsingDefault && (
                        <Alert severity="info" variant="outlined">
                            Using the auto-generated key{' '}
                            <Box
                                component="span"
                                sx={{ fontFamily: 'monospace' }}
                            >
                                {defaultKey}
                            </Box>
                            . The English title entered in the category modal
                            will be saved under this key.
                        </Alert>
                    )}

                    {matchingOption && (
                        <Alert severity="warning" variant="outlined">
                            Reusing existing key with{' '}
                            {matchingOption.languageCount} translation
                            {matchingOption.languageCount === 1 ? '' : 's'} on
                            file. Its translations are saved to the portal when
                            you save the category, and the English title field
                            becomes read-only so they aren't accidentally
                            overwritten — edit them from the Portal Translations
                            page if needed.
                        </Alert>
                    )}

                    {isNewKey && (
                        <Alert severity="info" variant="outlined">
                            A new key will be created on save. The English title
                            entered in the category modal will be saved under
                            this key so it shows up under Portal Translations.
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleReset}
                    color="secondary"
                    disabled={isUsingDefault}
                >
                    Reset to auto-generated
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <Button onClick={handleApply} variant="contained">
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};
